import { message } from "antd";
import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import {
  apiClient,
  LeaseDetailsLoaderResult,
} from "~/services/apiClient";
import { sendAdvanceLeaseIntent } from "~/services/lease/advanceLeaseIntent";
import { LoaderData } from "~/types";
import {
  FormattedLeaseData,
  InitiateRenewalPayload,
  LeaseFormValues,
  AdvanceLeasePayload,
  CancelLeasePayload,
} from "~/types";
import {
  INTENTS,
  getRouteWithParams,
  loadCookiesData,
  resolveActorUserName,
} from "~/utils";
import { withEntityCacheInvalidation } from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";
import i18n from "~/i18n";

export const LeaseDetailsPageLoader = async ({
  request,
  params,
}: LoaderFunctionArgs) => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) {
    redirect("/explore");
    return;
  }
  const userUID = user.uid;
  const authAccessInfo = await loadCookiesData(userUID);
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID, ownerType } = authAccessInfo.data;

  const leaseID = params.leaseID || "";
  const result = await apiClient.loader<LeaseDetailsLoaderResult>(
    "lease-details",
    {
      id: leaseID,
      ownerID,
      ownerType,
      userID: userUID,
    }
  );

  if (!result.success || !result.data) {
    console.error("Failed to load lease details:", result.error);
    throw new Error(result.error ?? "Failed to load lease details");
  }

  const data = result.data;
  const userName = await resolveActorUserName(user, authAccessInfo);

  return {
    unitDetails: { success: true, data: data.unitDetails },
    ownerID: userUID,
    ownerType: APITypes.OwnerType.User,
    userName,
    formattedLease: { success: true, data: data.formattedLease },
    nextLease: data.nextLease
      ? { success: true, data: data.nextLease }
      : { success: true, data: null },
  };
};

export type LeaseDetailsPageLoaderType = {
  unitDetails: LoaderData<APITypes.Unit>;
  ownerID: string;
  ownerType: APITypes.OwnerType;
  userName: string;
  formattedLease: LoaderData<FormattedLeaseData>;
  nextLease: LoaderData<FormattedLeaseData | null>;
};

export const LeaseDetailsPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  // const auth = getAuth();
  // const user = await getReadyAuthUser();
  // const userInfo = await fetchUser();
  const leaseID = params.leaseID || "";

  const leaseCacheTargets = [
    { entity: "lease" as const, entityID: leaseID },
    { entity: "unit" as const },
  ];
  const mutateLease = <T,>(fn: () => Promise<T>) =>
    withEntityCacheInvalidation(leaseCacheTargets, fn);

  switch (intent) {
    case INTENTS._normalize_rent_cycle: {
      return mutateLease(async () => {
        const payload = JSON.parse(
          formData.get("body") as string
        ) as { leaseID: string; settledThrough?: string };

        const result = await apiClient.sendIntent(INTENTS._normalize_rent_cycle, {
          leaseID: payload.leaseID,
          ...(payload.settledThrough
            ? { settledThrough: payload.settledThrough }
            : {}),
        });

        if (result.success) {
          return { success: true, data: result.data };
        }
        return {
          success: false,
          error: result.error ?? i18n.t("Sorry_something_went_wrong"),
        };
      });
    }
    case INTENTS._initiate_renewal_lease: {
      return mutateLease(async () => {
        const payload = JSON.parse(
          formData.get("body") as string
        ) as InitiateRenewalPayload & { id: string; userName?: string };

        const result = await apiClient.sendIntent<{
          newLeaseID?: string;
        }>(INTENTS._initiate_renewal_lease, {
          id: payload.id,
          lease: payload.lease,
          startDatePicker: payload.startDatePicker,
          endDatePicker: payload.endDatePicker,
          monthlyRentAmount: payload.monthlyRentAmount,
          comment: payload.comment,
          userName: payload.userName,
          normalizeRent: Boolean(
            (payload as { normalizeRent?: boolean }).normalizeRent,
          ),
        });

        if (result.success) {
          message.success(i18n.t("Form_submitted_successfully"));
          return { success: true, data: result.data };
        }
        return {
          success: false,
          error: result.error ?? i18n.t("Sorry_something_went_wrong"),
        };
      });
    }
    case INTENTS._edit_lease: {
      return mutateLease(async () => {
        const payload = JSON.parse(
          formData.get("body") as string
        ) as LeaseFormValues & { _version?: number };

        const result = await apiClient.sendIntent<APITypes.Lease>(
          INTENTS._edit_lease,
          {
            id: leaseID,
            type: payload.type,
            status: payload.status,
            startDate: payload.startDate,
            endDate: payload.endDate,
            markedDate: payload.markedDate,
            _version: payload._version,
            offerStatus: payload.offerStatus,
            rent: payload.rent,
          }
        );

        if (result.success && result.data) {
          return { success: true, data: result.data };
        }
        return {
          success: false,
          data: {} as APITypes.Lease,
          error: result.error ?? "Failed to update lease",
        };
      });
    }
    case INTENTS._advance_lease: {
      return mutateLease(async () => {
        const payload = JSON.parse(
          formData.get("body") as string
        ) as AdvanceLeasePayload;

        const result = await sendAdvanceLeaseIntent(leaseID, payload);
        if (result.success) {
          message.success(i18n.t("Form_submitted_successfully"));
        } else {
          message.error(result.error ?? i18n.t("Sorry_something_went_wrong"));
        }
        return result;
      });
    }
    case INTENTS._cancel_lease_actions: {
      return mutateLease(async () => {
        const payload = JSON.parse(
          formData.get("body") as string
        ) as CancelLeasePayload;

        return apiClient.sendIntent<Record<string, unknown>>(
          INTENTS._cancel_lease_actions,
          {
            id: leaseID,
            cancelAction: payload.cancelAction,
            applicationID: payload.applicationID,
            applicationVersion: payload.applicationVersion,
            backgroundCheckID: payload.backgroundCheckID,
            backgroundCheckVersion: payload.backgroundCheckVersion,
            offerID: payload.offerID,
            offerVersion: payload.offerVersion,
            moveInID: payload.moveInID,
            moveInVersion: payload.moveInVersion,
            moveOutID: payload.moveOutID,
            moveOutVersion: payload.moveOutVersion,
            entityID: payload.entityID,
            leaseID,
            leaseVersion: payload.leaseVersion,
          }
        );
      });
    }
    case INTENTS._delete_lease: {
      return mutateLease(async () => {
        const result = await apiClient.sendIntent<{
          nextLeaseID?: string;
          deleted?: boolean;
          unitUpdated?: boolean;
          tenantUpdated?: boolean;
          prevLeaseUpdated?: boolean;
        }>(INTENTS._delete_lease, { id: leaseID });

        if (result.success && result.data?.nextLeaseID) {
          message.error(
            i18n.t(
              "This_lease_has_been_renewed_Please_delete_the_next_lease_first"
            )
          );
          return redirect(
            getRouteWithParams("leaseDetails", {
              leaseID: result.data.nextLeaseID,
            })
          );
        }
        if (result.success && result.data?.deleted) {
          message.success(i18n.t("Lease_deleted_successfully"));
          if (result.data.unitUpdated) {
            message.success(i18n.t("Unit_marked_as_vacant_successfully"));
          }
          if (
            result.data.unitUpdated === false ||
            result.data.tenantUpdated === false ||
            result.data.prevLeaseUpdated === false
          ) {
            message.error(i18n.t("Something_went_wrong_Please_try_again"));
          }
          return redirect("/");
        }
        message.error(result.error ?? i18n.t("Failed_to_delete_lease"));
        return result;
      });
    }
  }
  return null;
};
